import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { ArrowLeft, CheckCircle, Database, Eye, EyeOff, Headphones, Lock, Mail, Monitor, ShieldCheck, Server, LogIn } from 'lucide-react'
import ThemeToggle from '../components/ThemeToggle'
import { updateDynamicFavicon } from '../utils/operational'

function BrandLogo({ logo, empresa }) {
  if (logo) {
    return (
      <div className="login-brand-logo" aria-label={empresa}>
        <img src={`data:image/png;base64,${logo}`} alt={empresa}/>
      </div>
    )
  }
  return (
    <div className="login-brand-fallback" aria-label={empresa}>
      <span>A</span><i/><span>T</span>
      <strong>Aden</strong>
      <small>Technology</small>
    </div>
  )
}

function CircuitIcon({ side, top, icon: Icon, lines = 3 }) {
  return (
    <div className={`login-circuit login-circuit--${side}`} style={{top}}>
      <div className="login-circuit-icon"><Icon size={32}/></div>
      <div className="login-circuit-lines" data-lines={lines}>
        <span/><span/><span/>
      </div>
    </div>
  )
}

function CircuitBackground() {
  return (
    <div className="login-circuit-layer" aria-hidden="true">
      <CircuitIcon side="left" top="22%" icon={Headphones}/>
      <CircuitIcon side="left" top="51%" icon={Server}/>
      <CircuitIcon side="left" top="78%" icon={Monitor}/>
      <CircuitIcon side="right" top="28%" icon={Monitor}/>
      <CircuitIcon side="right" top="55%" icon={ShieldCheck}/>
      <CircuitIcon side="right" top="80%" icon={Database}/>
    </div>
  )
}

function Field({ id, label, icon: Icon, children }) {
  return (
    <label htmlFor={id} className="login-field-block">
      <span>{label}</span>
      <div className="login-field">
        <Icon size={22}/>
        {children}
      </div>
    </label>
  )
}

export default function Login() {
  const { login } = useAuth()
  const nav = useNavigate()
  const [params] = useSearchParams()
  const [logo, setLogo] = useState(null)
  const [empresa, setEmpresa] = useState('Aden Technology')
  const [tela, setTela] = useState(params.get('token') ? 'redefinir' : 'login')
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [mfa, setMfa] = useState('')
  const [novaSenha, setNovaSenha] = useState('')
  const [confirm, setConfirm] = useState('')
  const [show, setShow] = useState(false)
  const [err, setErr] = useState('')
  const [msg, setMsg] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const token = params.get('token') || ''

  useEffect(() => {
    fetch('/api/configuracao/publica')
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return
        if (d.logo_base64) setLogo(d.logo_base64)
        if (d.nome_empresa) setEmpresa(d.nome_empresa)
        if (d.favicon_svg) updateDynamicFavicon(d.favicon_svg)
      })
      .catch(() => {})
  }, [])

  const handleLogin = async e => {
    e.preventDefault()
    setErr('')
    setSubmitting(true)
    try {
      const r = await login(email, senha)
      if (r.ok) nav('/')
      else if (r.mfaRequired) { setMfa(''); setTela('mfa'); setMsg('') }
      else setErr(r.msg)
    } finally { setSubmitting(false) }
  }

  const handleMfa = async e => {
    e.preventDefault()
    setErr('')
    setSubmitting(true)
    try {
      const r = await login(email, senha, mfa)
      if (r.ok) nav('/')
      else setErr(r.msg)
    } finally { setSubmitting(false) }
  }

  const handleRecuperar = async e => {
    e.preventDefault()
    setErr('')
    setSubmitting(true)
    try {
      const r = await fetch('/api/auth/recuperar-senha?email=' + encodeURIComponent(email), { method:'POST' })
      const d = await r.json()
      if (d.ok) setTela('sucesso')
      else setErr(d.detail || 'Erro ao processar.')
    } catch {
      setErr('Erro de conexão')
    } finally {
      setSubmitting(false)
    }
  }

  const handleRedefinir = async e => {
    e.preventDefault()
    setErr('')
    if (novaSenha !== confirm) { setErr('As senhas não coincidem'); return }
    if (novaSenha.length < 6) { setErr('Mínimo de 6 caracteres'); return }
    setSubmitting(true)
    try {
      const r = await fetch(`/api/auth/redefinir-senha?token=${encodeURIComponent(token)}&nova_senha=${encodeURIComponent(novaSenha)}`, { method:'POST' })
      const d = await r.json()
      if (d.ok) { setTela('login'); setMsg('Senha redefinida. Faça login.') }
      else setErr(d.detail || 'Token inválido ou expirado')
    } catch {
      setErr('Erro de conexão')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main id="conteudo-principal" className="login-board" aria-label="Acesso ao sistema">
      <CircuitBackground/>
      <div className="login-theme-control"><ThemeToggle/></div>

      <section className="login-stage">
        <BrandLogo logo={logo} empresa={empresa}/>

        <div className="login-card">
          {tela === 'login' && (
            <>
              <div className="login-shield"><ShieldCheck size={58}/></div>
              <h1>Acesse sua conta</h1>
              <p className="login-help text-xs text-slate-400 mb-2">Plataforma de Help Desk, Gestão de Serviços de TI e Agendamento da Aden Technology.</p>
              <div className="login-divider"/>

              {msg && <div className="login-message login-message--ok">{msg}</div>}
              {err && <div className="login-message login-message--error">{err}</div>}

              <form onSubmit={handleLogin} className="login-form">
                <Field id="login-email" label="E-mail" icon={Mail}>
                  <input id="login-email" name="email" type="email" autoComplete="username" value={email} onChange={e=>setEmail(e.target.value)} required placeholder="seu@email.com"/>
                </Field>
                <Field id="login-senha" label="Senha" icon={Lock}>
                  <input id="login-senha" name="password" type={show ? 'text' : 'password'} autoComplete="current-password" value={senha} onChange={e=>setSenha(e.target.value)} required placeholder="••••••••••••"/>
                  <button type="button" className="login-eye min-w-[44px] min-h-[44px] inline-flex items-center justify-center" onClick={()=>setShow(v=>!v)} aria-label={show ? 'Ocultar senha' : 'Mostrar senha'}>
                    {show ? <EyeOff size={22}/> : <Eye size={22}/>}
                  </button>
                </Field>
                <button type="button" className="login-forgot min-h-[44px] inline-flex items-center" onClick={()=>{setTela('recuperar');setErr('');setMsg('')}}>
                  Esqueci minha senha
                </button>
                <button type="submit" disabled={submitting} className="login-submit min-h-[48px] active:scale-[0.97] transition-transform">
                  {submitting ? 'Entrando...' : <><LogIn size={26}/>Entrar</>}
                </button>
              </form>
            </>
          )}

          {tela === 'mfa' && (
            <>
              <button type="button" onClick={()=>{setTela('login');setMfa('');setErr('')}} className="login-back min-h-[44px] inline-flex items-center"><ArrowLeft size={16}/>Voltar ao login</button>
              <div className="login-shield"><ShieldCheck size={58}/></div>
              <h1>Verificação em duas etapas</h1>
              <p className="login-help">Digite o código do aplicativo autenticador ou use um código de recuperação.</p>
              <div className="login-divider"/>
              {err && <div className="login-message login-message--error">{err}</div>}
              <form onSubmit={handleMfa} className="login-form">
                <Field id="mfa-code" label="Código de autenticação" icon={ShieldCheck}>
                  <input id="mfa-code" name="mfa" autoFocus value={mfa} onChange={e=>setMfa(e.target.value.replace(/\s/g,''))} inputMode="numeric" autoComplete="one-time-code" placeholder="000000 ou código de recuperação" required/>
                </Field>
                <button type="submit" disabled={submitting || !mfa.trim()} className="login-submit min-h-[48px] active:scale-[0.97] transition-transform">
                  {submitting ? 'Verificando...' : <><ShieldCheck size={24}/>Verificar e entrar</>}
                </button>
              </form>
            </>
          )}

          {tela === 'recuperar' && (
            <>
              <button type="button" onClick={()=>{setTela('login');setErr('')}} className="login-back min-h-[44px] inline-flex items-center"><ArrowLeft size={16}/>Voltar ao login</button>
              <h1>Recuperar senha</h1>
              <p className="login-help">Informe seu e-mail e enviaremos um link para criar uma nova senha.</p>
              {err && <div className="login-message login-message--error">{err}</div>}
              <form onSubmit={handleRecuperar} className="login-form">
                <Field id="recuperar-email" label="E-mail cadastrado" icon={Mail}>
                  <input id="recuperar-email" name="email" type="email" autoComplete="email" value={email} onChange={e=>setEmail(e.target.value)} required placeholder="seu@email.com"/>
                </Field>
                <button type="submit" disabled={submitting} className="login-submit min-h-[48px] active:scale-[0.97] transition-transform">{submitting ? 'Enviando...' : 'Enviar link de recuperação'}</button>
              </form>
            </>
          )}

          {tela === 'sucesso' && (
            <div className="login-success">
              <CheckCircle size={58}/>
              <h1>Link enviado</h1>
              <p className="login-help">Verifique sua caixa de entrada e spam. O link expira em 30 minutos.</p>
              <button type="button" onClick={()=>setTela('login')} className="login-submit min-h-[48px]">Voltar ao login</button>
            </div>
          )}

          {tela === 'redefinir' && (
            <>
              <h1>Nova senha</h1>
              <p className="login-help">Escolha uma senha com pelo menos 6 caracteres.</p>
              {err && <div className="login-message login-message--error">{err}</div>}
              <form onSubmit={handleRedefinir} className="login-form">
                <Field id="redefinir-nova-senha" label="Nova senha" icon={Lock}>
                  <input id="redefinir-nova-senha" name="new-password" type="password" autoComplete="new-password" value={novaSenha} onChange={e=>setNovaSenha(e.target.value)} required placeholder="Mínimo 6 caracteres"/>
                </Field>
                <Field id="redefinir-confirmar-senha" label="Confirmar senha" icon={Lock}>
                  <input id="redefinir-confirmar-senha" name="confirm-password" type="password" autoComplete="new-password" value={confirm} onChange={e=>setConfirm(e.target.value)} required placeholder="Repita a senha"/>
                </Field>
                <button type="submit" disabled={submitting} className="login-submit">{submitting ? 'Salvando...' : 'Redefinir senha'}</button>
              </form>
            </>
          )}
        </div>

        <footer className="login-footer flex flex-col gap-2 items-center text-center max-w-lg mx-auto">
          <p className="text-[11px] leading-relaxed opacity-80" style={{ color: 'var(--text2)' }}>
            <strong>Aden Technology</strong> — Gestão de Atendimento, Infraestrutura de TI e Agendamento Técnico Integrado.
          </p>
          <div className="flex items-center gap-3 text-xs">
            <Link to="/privacidade" className="hover:underline text-blue-400 hover:text-blue-300 transition-colors">Política de Privacidade</Link>
            <span className="opacity-40">•</span>
            <Link to="/termos" className="hover:underline text-blue-400 hover:text-blue-300 transition-colors">Termos de Serviço</Link>
          </div>
          <p className="text-[10px] opacity-60">
            © {new Date().getFullYear()} Aden Technology. Todos os direitos reservados.
          </p>
        </footer>
      </section>
    </main>
  )
}
